import React, { useCallback, useRef } from 'react';

import { MimeType } from '@aline/common';
import { Alert, Button, CircularProgress } from '@mui/material';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { SkillPdfPreview } from './skill-pdf-preview';
import type { SkillPreviewExtension } from './skill-pdf-preview';
import {
  decodeSkillAttachmentBytes,
  getSkillAttachmentIdentity,
  useSkillAttachmentContent,
} from './use-skill-attachment-content';

/** Where the previewed file comes from. */
type SkillResourceSource =
  /** A file stored in the skill: the author's draft, or the published version for others. */
  | { kind: 'saved'; path: string; skill: Apollo.AgentSkillSimpleFragment }
  /** A file picked on this device and not saved yet. */
  | { base64: string; contentType: string; kind: 'upload' }
  /** Text that is already at hand. */
  | { kind: 'text'; text: string };

export type SkillResourcePreviewProps = SkillResourceSource & {
  controlsContainer?: HTMLElement | null;
  snapshotDocumentId?: string;
};

type ApryseResource = {
  bytes: Uint8Array<ArrayBuffer>;
  extension: SkillPreviewExtension;
  status: 'apryse';
};

type DecodedResource =
  | ApryseResource
  | { status: 'empty' }
  | { status: 'failed' }
  | { status: 'text'; text: string };

type PendingText = {
  readonly documentId: string;
  readonly savedIdentity: string | undefined;
  readonly text: string;
};

const useStyles = makeStyles()(theme => ({
  canvas: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: '24px',
    [theme.breakpoints.down('sm')]: { padding: 12 },
  },
  page: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
    boxSizing: 'border-box',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    fontSize: 14,
    lineHeight: 1.85,
    margin: '0 auto',
    maxWidth: 800,
    minHeight: 360,
    overflowWrap: 'anywhere',
    padding: '40px 48px',
    whiteSpace: 'pre-wrap',
    [theme.breakpoints.down('sm')]: { padding: 20 },
  },
}));

const TextPage = React.memo(function TextPage({ text }: { text: string }) {
  const { classes } = useStyles();
  return (
    <div className={classes.canvas}>
      <pre className={classes.page}>{text}</pre>
    </div>
  );
});

const LoadError = React.memo(function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <Alert action={<Button onClick={onRetry}>Retry</Button>} severity="error">
      Could not load this file.
    </Alert>
  );
});

/** Stays mounted while the saved file is verified or fails, so the reader keeps its page. */
const ApryseResourcePreview = React.memo(function ApryseResourcePreview({
  controlsContainer,
  failed,
  onRetry,
  pendingText,
  resource,
  verifying,
}: {
  controlsContainer: HTMLElement | null | undefined;
  failed: boolean;
  onRetry: () => void;
  pendingText: string | undefined;
  resource: ApryseResource;
  verifying: boolean;
}) {
  const hidden = failed || verifying;
  return (
    <>
      {failed && <LoadError onRetry={onRetry} />}
      {verifying && <CircularProgress size={24} />}
      <div aria-hidden={hidden} style={{ visibility: hidden ? 'hidden' : 'visible' }}>
        <SkillPdfPreview
          bytes={resource.bytes}
          controlsContainer={hidden ? null : controlsContainer}
          extension={resource.extension}
          loadingFallback={pendingText !== undefined && <TextPage text={pendingText} />}
        />
      </div>
    </>
  );
});

export const SkillResourcePreview = React.memo(function SkillResourcePreview(
  props: SkillResourcePreviewProps
) {
  const { classes } = useStyles();
  const saved = props.kind === 'saved' ? props : null;
  const {
    content: stored,
    error,
    loading,
    refetch,
  } = useSkillAttachmentContent({
    path: saved?.path ?? '',
    skill: saved?.skill ?? null,
    skip: !saved,
  });
  const pendingText = usePendingText(props);
  const decoded = useDecodedResource(
    props.kind === 'upload'
      ? { contentType: props.contentType, encoded: props.base64 }
      : { contentType: stored?.contentType, encoded: stored?.base64 }
  );
  const failed = Boolean(error) || decoded.status === 'failed';

  const retry = useCallback(() => {
    // The query error remains visible if the retry also fails.
    void refetch().catch(() => undefined);
  }, [refetch]);

  if (props.kind === 'text') return <TextPage text={props.text} />;
  if (!failed && loading && pendingText !== undefined) return <TextPage text={pendingText} />;
  if (decoded.status === 'apryse')
    return (
      <ApryseResourcePreview
        controlsContainer={props.controlsContainer}
        failed={failed}
        pendingText={pendingText}
        resource={decoded}
        verifying={Boolean(saved && loading)}
        onRetry={retry}
      />
    );
  if (loading) return <CircularProgress size={24} />;
  if (failed) return <LoadError onRetry={retry} />;
  switch (decoded.status) {
    case 'empty':
      return <div className={classes.canvas} />;
    case 'text':
      return <TextPage text={decoded.text} />;
  }
});

/**
 * Remembers the text of a document copy so it stays on screen while the saved file for the
 * same document loads. It is dropped once another document or another saved file is shown.
 */
function usePendingText(props: SkillResourcePreviewProps) {
  const pendingRef = useRef<PendingText | null>(null);
  const pending = pendingRef.current;
  const savedIdentity =
    props.kind === 'saved'
      ? getSkillAttachmentIdentity({ path: props.path, skill: props.skill })
      : undefined;
  const isSameSavedDocument =
    pending !== null &&
    savedIdentity !== undefined &&
    pending.documentId === props.snapshotDocumentId &&
    (!pending.savedIdentity || pending.savedIdentity === savedIdentity);

  if (props.kind === 'text' && props.snapshotDocumentId)
    pendingRef.current = {
      documentId: props.snapshotDocumentId,
      savedIdentity: undefined,
      text: props.text,
    };
  else if (isSameSavedDocument) pendingRef.current = { ...pending, savedIdentity };
  else pendingRef.current = null;
  return pendingRef.current?.text;
}

// Keeps the same byte buffer when a pending upload becomes an identical saved file.
function useDecodedResource({
  contentType,
  encoded,
}: {
  contentType: string | undefined;
  encoded: string | undefined;
}): DecodedResource {
  const lastRef = useRef<{
    contentType: string | undefined;
    decoded: DecodedResource;
    encoded: string;
  } | null>(null);
  const last = lastRef.current;
  if (encoded && (last?.encoded !== encoded || last.contentType !== contentType))
    lastRef.current = { contentType, decoded: decodeResource({ contentType, encoded }), encoded };
  return lastRef.current?.decoded ?? { status: 'empty' };
}

function decodeResource({
  contentType,
  encoded,
}: {
  contentType: string | undefined;
  encoded: string;
}): DecodedResource {
  try {
    const bytes = decodeSkillAttachmentBytes(encoded);
    if (contentType === MimeType.PDF) return { bytes, extension: 'pdf', status: 'apryse' };
    if (contentType === MimeType.DOCX) return { bytes, extension: 'docx', status: 'apryse' };
    return { status: 'text', text: new TextDecoder().decode(bytes) };
  } catch {
    return { status: 'failed' };
  }
}
